import client from './feathers';

/**
 * Admin impersonation, client side.
 *
 * The switch is a token swap in place: the admin's own JWT is stashed, the
 * impersonation JWT becomes the active one, and "Switch back" restores the
 * stash. No second login, no magic link, and nothing to leak — the token never
 * travels through a URL.
 *
 * The stash lives in localStorage rather than memory so a refresh (or opening
 * the listing in a new tab, which admins do constantly) doesn't strand the
 * session with no way back to your own account.
 */
const STASH_KEY = 'vs-impersonation';
// Where @feathersjs/authentication-client keeps the active token.
const TOKEN_KEY = 'feathers-jwt';

/**
 * Fired whenever a session starts or ends. The banner is mounted in the app
 * shell, so it may already have read an empty stash by the time one is
 * written — without this it would stay hidden until something remounted it.
 */
export const IMPERSONATION_EVENT = 'vs-impersonation-changed';

function notifyChange() {
  window.dispatchEvent(new Event(IMPERSONATION_EVENT));
}

function readStash() {
  try {
    const raw = window.localStorage.getItem(STASH_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

/** The live session, or null. Expired sessions read as null. */
export function getImpersonation() {
  const stash = readStash();
  if (!stash) return null;
  if (stash.expiresAt && new Date(stash.expiresAt).getTime() <= Date.now()) return null;
  return stash;
}

export function isImpersonating() {
  return getImpersonation() !== null;
}

/**
 * Start acting as `userId`. Returns the target user.
 *
 * Order matters: the admin's token is captured BEFORE the swap, and the stash
 * is written BEFORE the new token goes active. If anything fails after that
 * point the admin can still get back — a half-applied switch that loses the
 * original token would mean re-logging-in to escape.
 */
export async function startImpersonation(userId) {
  const adminToken = await client.authentication.getAccessToken();
  if (!adminToken) throw new Error('Not signed in.');

  const result = await client.service('admin/impersonation').create({ userId });

  window.localStorage.setItem(STASH_KEY, JSON.stringify({
    adminToken,
    admin: result.admin,
    sessionId: result.session.id,
    target: result.user,
    startedAt: result.session.startedAt,
    expiresAt: result.expiresAt,
  }));

  await client.authentication.setAccessToken(result.accessToken);
  await client.reAuthenticate(true);
  notifyChange();

  return result.user;
}

/**
 * Stop impersonating and restore the admin's own session.
 *
 * Closing the session row is best-effort: if that call fails the admin is
 * still returned to their own account, which is the part that matters. The row
 * then simply ends at its expiry instead of at the switch-back.
 */
export async function endImpersonation() {
  const stash = readStash();
  window.localStorage.removeItem(STASH_KEY);

  if (!stash || !stash.adminToken) {
    // Nothing to restore — drop the impersonation token rather than leaving it
    // active, and let the caller send the user back to the login screen.
    window.localStorage.removeItem(TOKEN_KEY);
    notifyChange();
    return null;
  }

  await client.authentication.setAccessToken(stash.adminToken);
  const auth = await client.reAuthenticate(true);

  if (stash.sessionId) {
    try {
      await client.service('admin/impersonation').remove(stash.sessionId);
    } catch (e) {
      // Non-fatal: see the note above.
    }
  }

  notifyChange();
  return auth.user;
}

/**
 * Ask for a one-time link instead of switching in place.
 *
 * Nothing changes about the current session — that's the point. The admin stays
 * signed in as themselves and pastes the link into a private window, so both
 * views are open at once.
 */
export async function createImpersonationLink(userId, surface = 'admin') {
  return client.service('admin/impersonation').create({ userId, mode: 'link', surface });
}

/**
 * Redeem a one-time link in THIS browser (the private window).
 *
 * There is no admin token to stash here — this window was never signed in as
 * an admin, which is exactly why the link exists. The stash is written anyway,
 * without a token, so the banner knows to offer "End session" rather than
 * "Switch back".
 */
export async function redeemImpersonationLink(code) {
  const result = await client.service('impersonation-redeem').create({ code });

  window.localStorage.setItem(STASH_KEY, JSON.stringify({
    mode: 'link',
    adminToken: null,
    sessionId: result.session.id,
    target: result.user,
    startedAt: result.session.startedAt,
    expiresAt: result.expiresAt,
  }));

  await client.authentication.setAccessToken(result.accessToken);
  await client.reAuthenticate(true);
  notifyChange();

  return result.user;
}

/** Display name for the banner, falling back through what the user has set. */
export function impersonationLabel(target) {
  if (!target) return 'someone';
  const full = [target.firstName, target.lastName].filter(Boolean).join(' ').trim();
  return full || (target.username ? `@${target.username}` : target.email) || 'someone';
}
